import { useState, useEffect, useMemo, useRef } from 'react';
import { iconsPath, addcomponent } from '../../utils/imagePath';
import { useSelector, useDispatch } from 'react-redux';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { addEvent, removeEvent } from '../../redux/Slice/gridSlice';
import {
  setIdToDelete,
  setIsConfirmationOpen,
  setEventsPinned,
} from '../../redux/Slice/globalStates';
import { CustomDropdownWithLabel, SimpleAssetTooltip } from '../../utils/gridUtils';
import { updateAnimationObject } from '../../redux/Slice/animationSlice';
// import { CustomDropdownWithLabel } from '../../utils/gridUtils';
import ConfirmationModel from '../ConfirmationModel';
import {
  attachEventsToSceneObject,
  getEventsForSceneObject,
  hydrateSceneObjectEvents,
  normalizeEventTrigger,
  EVENT_TRIGGER_TYPES,
  updateAssetEventsInRedux,
} from '../../utils/eventAssetUtils';

const VR_MR_REPLACE_CONFIRM_TITLE = 'Replace existing event?';
const VR_MR_REPLACE_CONFIRM_MESSAGE =
  'This object already has an event. Replace it with the new one?';

const resolveSceneObjectForAsset = (scene, asset) => {
  if (!scene || !asset) return null;
  const ids = [
    asset._id,
    asset.id,
    asset.url,
    asset.name,
    asset.temp_file_name,
  ].filter(Boolean);
  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (obj) return obj;
  }
  return scene.getObjectByName(asset.asset_file_name || asset.file_name);
};

const EventsComponent = ({
  scene,
  completeImageObject,
  category,
}) => {
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [noTriggerError, setNoTriggerError] = useState(false);
  const [selectedObject, setSelectedObject] = useState(
    completeImageObject?.selectedObject
  );
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [pendingReplaceEvent, setPendingReplaceEvent] = useState(null);

  // Form state
  const [inputType, setInputType] = useState('Mouse');
  const [trigger, setTrigger] = useState('Single Click');
  const [affectedAsset, setAffectedAsset] = useState(''); // Target object ID
  const [affectedAssetModel, setAffectedAssetModel] = useState(''); // Target object type
  const [action, setAction] = useState('');
  const [url, setUrl] = useState('');
  const [triggeringObject, setTriggeringObject] = useState(''); // New state for triggering object

  const dispatch = useDispatch();
  const { assets_data } = useSelector((state) => state.assets_object);
  const particles = useSelector((state) => state.particles?.particles || []);
  const eventsRootRef = useRef(null);
  const newEventFormRef = useRef(null);
  const eventRowRefs = useRef(new Map());
  const [lastAddedEventId, setLastAddedEventId] = useState(null);

  const particleAssets = useMemo(
    () =>
      particles.map((particle) => ({
        ...particle,
        _id: particle._id || particle.id,
        type: 'Particle',
      })),
    [particles]
  );

  const activeObject =
    completeImageObject?.selectedObject || selectedObject || null;

  useEffect(() => {
    if (!activeObject) return;
    hydrateSceneObjectEvents(activeObject, assets_data, scene);
  }, [
    activeObject,
    activeObject?.name,
    activeObject?._id,
    activeObject?.url,
    assets_data,
    scene,
  ]);

  const events = useMemo(() => {
    if (!activeObject) return [];
    return getEventsForSceneObject(activeObject, assets_data);
  }, [
    activeObject,
    activeObject?.name,
    activeObject?._id,
    activeObject?.url,
    activeObject?.events,
    activeObject?.userData?.events,
    assets_data,
  ]);

  const allAssets = [
    ...particleAssets,
    ...(assets_data?.image || []),
    ...(assets_data?.mesh || []),
    ...(assets_data?.video || []),
    ...(assets_data?.audio || []),
    ...(assets_data?.text || []),
  ];

  // Filter assets for target object dropdown (Mesh/Text/Image/Video/Audio/Particle)
  const targetObjectAssets = [
    ...particleAssets,
    ...(assets_data?.mesh?.map((asset) => ({ ...asset, type: 'Mesh' })) || []),
    ...(assets_data?.text?.map((asset) => ({ ...asset, type: 'Text' })) || []),
    ...(assets_data?.image?.map((asset) => ({ ...asset, type: 'Image' })) ||
      []),
    ...(assets_data?.video?.map((asset) => ({ ...asset, type: 'Video' })) ||
      []),
    ...(assets_data?.audio?.map((asset) => ({ ...asset, type: 'Audio' })) ||
      []),
  ];

  // Filter assets for triggering object dropdown
  const triggeringObjectAssets = [
    ...particleAssets,
    ...(assets_data?.mesh?.map((asset) => ({ ...asset, type: 'Mesh' })) || []),
    ...(assets_data?.text?.map((asset) => ({ ...asset, type: 'Text' })) || []),
    ...(assets_data?.image?.map((asset) => ({ ...asset, type: 'Image' })) ||
      []),
    ...(assets_data?.video?.map((asset) => ({ ...asset, type: 'Video' })) ||
      []),
    ...(assets_data?.audio?.map((asset) => ({ ...asset, type: 'Audio' })) ||
      []),
  ];

  const getAvailableActions = () => {
    if (!affectedAssetModel) {
      return ['Disable', 'Redirect to URL']; // Default actions when no target object
    }

    // Return actions based on asset type
    if (['Mesh', 'Text', 'Image', 'Particle'].includes(affectedAssetModel)) {
      return ['Disable', 'Enable', 'Play Animation', 'Redirect to URL'];
    } else if (['Video', 'Audio'].includes(affectedAssetModel)) {
      return ['Play', 'Pause', 'Stop', 'Disable', 'Redirect to URL'];
    }

    return ['Disable', 'Redirect to URL'];
  };

  // URL validation regex helper
  const isValidUrl = (string) => {
    try {
      const pattern = new RegExp(
        '^(https?:\\/\\/)' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
        'i'
      ); // fragment locator
      return !!pattern.test(string);
    } catch (e) {
      return false;
    }
  };

  const getBaseTriggersForCategory = () => {
    const activeCategory = (category || completeImageObject?.category || '').toLowerCase();
    if (activeCategory === 'vr' || activeCategory === 'mr') {
      return ['Gesture Pinch', 'Gesture Grab', 'Gesture Poke'];
    }
    if (!activeCategory) {
      return EVENT_TRIGGER_TYPES;
    }
    return ['Single Click', 'Double Click'];
  };

  const isVrOrMrCategory = () => {
    const activeCategory = (
      category ||
      completeImageObject?.category ||
      ''
    ).toLowerCase();
    return activeCategory === 'vr' || activeCategory === 'mr';
  };

  /** Drop previous events for this object (MR/VR: one event only). */
  const replaceObjectEventsWithSingle = (object, eventData) => {
    if (!object || !eventData) return;
    const previous = getEventsForSceneObject(object, assets_data);
    previous.forEach((existing) => {
      if (existing?.id && existing.id !== eventData.id) {
        dispatch(removeEvent(existing.id));
      }
    });
    attachEventsToSceneObject(object, [eventData]);
    updateAssetEventsInRedux(object, [eventData]);
  };

  // Initialize form values from selectedObject.events if available
  const getInitialFormValues = (object = activeObject) => {
    const objectEvents = object
      ? getEventsForSceneObject(object, assets_data)
      : [];
    const categoryTriggers = getBaseTriggersForCategory();
    const defaultTrigger = categoryTriggers[0] || 'Single Click';

    if (objectEvents.length > 0) {
      const lastEvent = objectEvents[objectEvents.length - 1];
      const normalizedTrigger = normalizeEventTrigger(lastEvent.trigger);
      return {
        inputType: lastEvent.inputType || 'Mouse',
        trigger: categoryTriggers.includes(normalizedTrigger)
          ? normalizedTrigger
          : defaultTrigger,
        action: lastEvent.action || '',
        affectedAsset: lastEvent.affectedAsset || '',
        affectedAssetModel: lastEvent.affectedAssetModel || '',
        url: lastEvent.url || '',
      };
    }
    // Default values
    return {
      inputType: 'Mouse',
      trigger: defaultTrigger,
      action: '',
      affectedAsset: '',
      affectedAssetModel: '',
      url: '',
    };
  };

  // Reset form to initial values
  const resetForm = (object = activeObject) => {
    const initial = getInitialFormValues(object);
    setInputType(initial.inputType);
    setTrigger(initial.trigger);
    setAction(initial.action);
    setAffectedAsset(initial.affectedAsset);
    setAffectedAssetModel(initial.affectedAssetModel);
    setUrl(initial.url);
  };

  const prevActiveObjectIdRef = useRef(null);

  // Sync selectedObject with completeImageObject?.selectedObject
  useEffect(() => {
    const newSelectedObject = completeImageObject?.selectedObject;
    const newId =
      newSelectedObject?.name ||
      newSelectedObject?._id ||
      newSelectedObject?.url ||
      null;

    if (newId === prevActiveObjectIdRef.current) return;
    prevActiveObjectIdRef.current = newId;

    setSelectedObject(newSelectedObject);

    if (newSelectedObject) {
      resetForm(newSelectedObject);
    }
  }, [completeImageObject?.selectedObject]);

  // Keep panel open when adding a new event
  useEffect(() => {
    if (showNewEventForm) {
      dispatch(setEventsPinned(true));
    }
  }, [showNewEventForm, dispatch]);

  // Update selectedObject.events when events change
  // useEffect(() => {
  //   if (selectedObject) {
  //     // Find events that belong to this selected object (as triggering object)
  //     const objectEvents = events.filter(
  //       (event) => event.triggeringObject === selectedObject._id
  //     );
  //     selectedObject.events = objectEvents;
  //   }
  // }, [events, selectedObject]);

  // Check if form is valid based on action type
  const isFormValid = () => {
    const baseValid = activeObject && inputType && trigger && action;

    if (action === 'Redirect to URL') {
      return baseValid && url.trim() !== '' && isValidUrl(url);
    }

    // For other actions, we need a target object
    if (action && action !== 'Redirect to URL') {
      return baseValid && affectedAsset;
    }

    return baseValid;
  };

  const handleAddNewEventType = () => {
    const opening = !showNewEventForm;
    setShowNewEventForm(opening);
    if (opening) {
      resetForm();
    }
  };

  const scrollNodeIntoPanel = (node) => {
    if (!node) return;

    let parent = node.parentElement;
    while (parent) {
      const { overflowY } = window.getComputedStyle(parent);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        const parentRect = parent.getBoundingClientRect();
        const targetRect = node.getBoundingClientRect();
        const nextTop =
          targetRect.top - parentRect.top + parent.scrollTop - 12;
        parent.scrollTo({
          top: Math.max(0, nextTop),
          behavior: 'smooth',
        });
        return;
      }
      parent = parent.parentElement;
    }

    node.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  };

  // When the new-event form opens, scroll the right panel so Events is visible.
  useEffect(() => {
    if (!showNewEventForm) return undefined;

    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        scrollNodeIntoPanel(newEventFormRef.current || eventsRootRef.current);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [showNewEventForm]);

  // After Add Event, scroll so the new / replaced event row is fully visible.
  useEffect(() => {
    if (!lastAddedEventId) return undefined;

    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const row = eventRowRefs.current.get(lastAddedEventId);
        scrollNodeIntoPanel(row || eventsRootRef.current);
        setLastAddedEventId(null);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [lastAddedEventId, events]);

  // Handle target object change
  const handleTargetObjectChange = (targetId) => {
    setAffectedAsset(targetId); // Set target object ID

    // Find the target asset and set its type
    const targetAsset = targetObjectAssets.find(
      (asset) => asset._id === targetId
    );

    if (targetAsset) {
      setAffectedAssetModel(targetAsset.type); // Set target object type
    } else {
      setAffectedAssetModel('');
    }

    // Reset action and URL when target object changes
    setAction('');
    setUrl('');
  };

  // Handle triggering object change
  const handleTriggeringObjectChange = (objectId) => {
    const selectedAsset = allAssets.find((asset) => asset._id === objectId);
    dispatch(setEventsPinned(true));
    selectObjectInScene(selectedAsset?._id || selectedAsset?.url);
  };

  // Initialize triggering object when component mounts or selectedObject changes
  useEffect(() => {
    if (selectedObject) {
      setTriggeringObject(
        selectedObject._id || selectedObject.name || selectedObject.url
      );
    }
  }, [selectedObject]);

  const commitVrMrSingleEvent = (targetObject, eventData) => {
    if (!targetObject || !eventData) return;

    const currentEvents = getEventsForSceneObject(targetObject, assets_data);
    previousPlayAnimationCleanupOnReplace(currentEvents, eventData);

    dispatch(addEvent(eventData));

    if (eventData.action === 'Play Animation') {
      dispatch(
        updateAnimationObject({
          id: eventData.affectedAsset,
          updates: { isActive: false },
        })
      );
    }

    replaceObjectEventsWithSingle(targetObject, eventData);

    const triggeringAsset = allAssets.find(
      (a) => a._id === eventData.triggeringObject
    );
    if (triggeringAsset) {
      const object = resolveSceneObjectForAsset(scene, triggeringAsset);
      if (object && object !== targetObject) {
        replaceObjectEventsWithSingle(object, eventData);
      }
    }

    resetFormAfterAdd(targetObject, eventData.id);
  };

  const handleAddEvent = () => {
    // Block if no triggers are available
    if (availableTriggers.length === 0) {
      setNoTriggerError(true);
      setTimeout(() => setNoTriggerError(false), 4000);
      return;
    }

    if (!isFormValid()) {
      return;
    }

    // Generate a unique ID for the event
    const eventId = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Prepare the event data
    const eventData = {
      id: eventId,
      inputType,
      affectedAsset,
      affectedAssetModel,
      trigger: normalizeEventTrigger(trigger),
      action,
      triggeringObject: triggeringObject,
      url: action === 'Redirect to URL' ? url : undefined,
    };

    // Check if this event already exists in the Redux store
    const existingEvent = events.find(
      (event) =>
        normalizeEventTrigger(event.trigger) === eventData.trigger &&
        event.action === action &&
        event.affectedAsset === affectedAsset
    );

    if (existingEvent) {
      return;
    }

    const targetObject = activeObject || selectedObject;
    const vrOrMr = isVrOrMrCategory();

    // MR/VR: one event per object — ask before replacing an existing event.
    if (vrOrMr && targetObject) {
      const currentEvents = getEventsForSceneObject(targetObject, assets_data);
      if (currentEvents.length > 0) {
        setPendingReplaceEvent(eventData);
        setIsReplaceModalOpen(true);
        return;
      }

      commitVrMrSingleEvent(targetObject, eventData);
      return;
    }

    // Dispatch to Redux store
    dispatch(addEvent(eventData));

    // If adding a Play Animation event, deactivate automatic animation
    if (action === 'Play Animation') {
      dispatch(
        updateAnimationObject({
          id: affectedAsset,
          updates: { isActive: false },
        })
      );
    }

    // Update the selectedObject's events array
    if (targetObject) {
      const currentEvents = getEventsForSceneObject(targetObject, assets_data);
      const eventExists = currentEvents.some(
        (event) =>
          event.triggeringObject === triggeringObject &&
          normalizeEventTrigger(event.trigger) === eventData.trigger &&
          event.action === action &&
          event.affectedAsset === affectedAsset
      );
      if (!eventExists) {
        const updatedEvents = [...currentEvents, eventData];
        attachEventsToSceneObject(targetObject, updatedEvents);
        updateAssetEventsInRedux(targetObject, updatedEvents);
      }
    }

    const triggeringAsset = allAssets.find((a) => a._id === triggeringObject);
    if (triggeringAsset) {
      const object = resolveSceneObjectForAsset(scene, triggeringAsset);
      if (object && object !== targetObject) {
        const currentEvents = getEventsForSceneObject(object, assets_data);
        const eventExists = currentEvents.some(
          (event) =>
            event.triggeringObject === triggeringObject &&
            normalizeEventTrigger(event.trigger) === eventData.trigger &&
            event.action === action &&
            event.affectedAsset === affectedAsset
        );
        if (!eventExists) {
          const updatedEvents = [...currentEvents, eventData];
          attachEventsToSceneObject(object, updatedEvents);
          updateAssetEventsInRedux(object, updatedEvents);
        }
      }
    }

    resetFormAfterAdd(targetObject, eventData.id);
  };

  const handleCancelReplaceEvent = () => {
    setIsReplaceModalOpen(false);
    setPendingReplaceEvent(null);
  };

  const handleConfirmReplaceEvent = () => {
    const targetObject = activeObject || selectedObject;
    if (pendingReplaceEvent && targetObject) {
      commitVrMrSingleEvent(targetObject, pendingReplaceEvent);
    }
    setIsReplaceModalOpen(false);
    setPendingReplaceEvent(null);
  };

  const previousPlayAnimationCleanupOnReplace = (currentEvents, newEvent) => {
    currentEvents.forEach((eventToRemove) => {
      if (eventToRemove.action !== 'Play Animation') return;
      // Re-activate auto-animation when the replaced event was Play Animation
      // and the new event is not Play Animation for the same asset.
      const stillNeeded =
        newEvent.action === 'Play Animation' &&
        newEvent.affectedAsset === eventToRemove.affectedAsset;
      if (!stillNeeded) {
        dispatch(
          updateAnimationObject({
            id: eventToRemove.affectedAsset,
            updates: { isActive: true },
          })
        );
      }
    });
  };

  const resetFormAfterAdd = (targetObject, addedEventId = null) => {
    const targetEvents = targetObject
      ? getEventsForSceneObject(targetObject, assets_data)
      : [];
    const categoryTriggers = getBaseTriggersForCategory();
    const nextTriggers = categoryTriggers.filter(
      (triggerOption) =>
        !targetEvents.some(
          (event) => normalizeEventTrigger(event.trigger) === triggerOption
        )
    );

    setInputType('Mouse');
    setTrigger(nextTriggers[0] || categoryTriggers[0] || 'Single Click');
    setAction('');
    setAffectedAsset('');
    setAffectedAssetModel('');
    setUrl('');
    if (addedEventId) {
      setLastAddedEventId(addedEventId);
    }
  };

  const handleRemoveEvent = (eventId) => {
    const eventToRemove = events.find((e) => e.id === eventId);

    if (!eventToRemove) {
      return;
    }

    // Dispatch to Redux store
    dispatch(removeEvent(eventId));

    // If removing a Play Animation event, check if we should re-activate automatic animation
    if (eventToRemove.action === 'Play Animation') {
      const otherPlayAnimEvents = events.filter(
        (e) =>
          e.id !== eventId &&
          e.action === 'Play Animation' &&
          e.affectedAsset === eventToRemove.affectedAsset
      );
      if (otherPlayAnimEvents.length === 0) {
        dispatch(
          updateAnimationObject({
            id: eventToRemove.affectedAsset,
            updates: { isActive: true },
          })
        );
      }
    }

    const targetObject = activeObject || selectedObject;
    if (targetObject) {
      const updatedEvents = (targetObject.events || []).filter(
        (event) => event.id !== eventId
      );
      attachEventsToSceneObject(targetObject, updatedEvents);
      updateAssetEventsInRedux(targetObject, updatedEvents);
    }

    // Update the 3D object in scene (when triggering asset differs from selection)
    const triggeringAsset = allAssets.find(
      (a) => a._id === eventToRemove.triggeringObject
    );
    if (triggeringAsset) {
      const object = resolveSceneObjectForAsset(scene, triggeringAsset);
      if (object && object !== targetObject) {
        const updatedEvents = (object.events || []).filter(
          (event) => event.id !== eventId
        );
        attachEventsToSceneObject(object, updatedEvents);
        updateAssetEventsInRedux(object, updatedEvents);
      }
    }

    // Reset any visual changes if needed
    if (eventToRemove.affectedAsset) {
      const targetAsset = allAssets.find(
        (a) => a._id === eventToRemove.affectedAsset
      );
      if (targetAsset) {
        const targetObject = scene.getObjectByName(
          targetAsset.asset_file_name || targetAsset.url
        );
        if (targetObject) {
          targetObject.visible = true;
          const element = document.getElementById(targetAsset._id);
          if (element) {
            element.style.opacity = '1';
          }
        }
      }
    }
  };

  const eventTriggerSignature = useMemo(
    () =>
      events
        .map(
          (event) =>
            `${event.id}:${normalizeEventTrigger(event.trigger)}`
        )
        .join('|'),
    [events]
  );

  const availableTriggers = useMemo(() => {
    const categoryTriggers = getBaseTriggersForCategory();
    if (!activeObject) return categoryTriggers;

    // MR/VR: one event per object — allow any trigger (add replaces the prior event).
    if (isVrOrMrCategory()) {
      return categoryTriggers;
    }

    return categoryTriggers.filter(
      (triggerOption) =>
        !events.some(
          (event) => normalizeEventTrigger(event.trigger) === triggerOption
        )
    );
  }, [
    activeObject,
    eventTriggerSignature,
    events,
    category,
    completeImageObject?.category,
  ]);

  // Initialize trigger state
  useEffect(() => {
    // Only set initial trigger if it's not already set and we have available triggers
    if (!trigger && availableTriggers.length > 0) {
      setTrigger(availableTriggers[0]);
    }
  }, []);

  const availableTriggerKey = availableTriggers.join('|');

  // Reset trigger when selected object or available triggers change
  useEffect(() => {
    if (!activeObject || availableTriggers.length === 0) return;
    if (!availableTriggers.includes(trigger)) {
      setTrigger(availableTriggers[0]);
    }
  }, [
    activeObject?.name,
    activeObject?._id,
    activeObject?.url,
    availableTriggerKey,
    trigger,
  ]);

  // Handle trigger change
  const handleTriggerChange = (newTrigger) => {
    const normalized = normalizeEventTrigger(newTrigger);
    if (availableTriggers.includes(normalized)) {
      setTrigger(normalized);
    }
  };

  return (
    <div ref={eventsRootRef}>
      <div className="mt-1 mb-1">
        <button
          onClick={handleAddNewEventType}
          className="w-full flex items-center justify-center py-1 px-2 bg-[#2B2C2F] hover:bg-[#2B2C2F] rounded text-xs text-gray-300"
        >
          <img
            className="add-component-img text-xs mr-2"
            src={iconsPath?.group290Image}
            alt=""
          />
          Add New Event Type
        </button>
      </div>

      {showNewEventForm && (
        <div
          ref={newEventFormRef}
          className="pl-2 pr-2 py-2 rounded bg-[#2B2C2F]"
        >
          <div className="text-xs font-medium mb-2">New Event Type</div>

          <CustomDropdownWithLabel
            label="Triggering Object"
            value={activeObject?.url || activeObject?.name}
            onChange={(e) => handleTriggeringObjectChange(e.target.value)}
            options={triggeringObjectAssets.map((a) => ({
              value: a._id,
              label: `${(a.file_name || a.asset_file_name || a.url || a.name || '').slice(0, 20)}${(a.file_name || a.asset_file_name || a.url || a.name || '').length > 20
                  ? '...'
                  : ''
                } (${a.type})`,
            }))}
            placeholder="Select triggering object"
            icon={iconsPath?.vectorImage}
          />

          {/* <CustomDropdownWithLabel
            label="Select an Input Type"
            value={inputType}
            onChange={(e) => setInputType(e.target.value)}
            options={[
              { value: 'Mouse', label: 'Mouse' },
              { value: 'Touch', label: 'Touch' },
            ]}
            icon={iconsPath?.vectorImage}
          /> */}

          <CustomDropdownWithLabel
            label="Trigger"
            value={trigger}
            onChange={(e) => handleTriggerChange(e.target.value)}
            options={
              availableTriggers.length
                ? availableTriggers.map((tr) => ({ value: tr, label: tr }))
                : [
                  {
                    value: '',
                    label: 'No triggers available',
                    disabled: true,
                  },
                ]
            }
            placeholder="Select trigger"
            icon={iconsPath?.vectorImage}
          />

          <CustomDropdownWithLabel
            label="Target Object"
            value={affectedAsset}
            onChange={(e) => handleTargetObjectChange(e.target.value)}
            options={targetObjectAssets.map((a) => ({
              value: a._id,
              label: `${(a.file_name || a.asset_file_name || a.url || a.name || '').slice(0, 20)}${(a.file_name || a.asset_file_name || a.url || a.name || '').length > 20
                  ? '...'
                  : ''
                } (${a.type})`,
            }))}
            placeholder="Select target object"
            icon={iconsPath?.vectorImage}
          />

          <CustomDropdownWithLabel
            label="Action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            options={getAvailableActions().map((act) => ({
              value: act,
              label: act,
            }))}
            placeholder={
              affectedAsset || action === 'Redirect to URL'
                ? 'Select action'
                : 'Select target object first'
            }
            icon={iconsPath?.vectorImage}
            disabled={!affectedAsset && action !== 'Redirect to URL'}
          />

          {action === 'Redirect to URL' && (
            <div className="mb-2">
              <label className="block text-gray-400 text-[10px] mb-1">
                URL Path
              </label>
              <input
                type="text"
                className="w-full bg-[#131414] rounded p-1 text-[10px] text-gray-400 focus:outline-none"
                placeholder="Enter URL (e.g., https://example.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              {url.trim() !== '' && !isValidUrl(url) && (
                <div className="mt-1 text-[9px] text-[#EE2B2A]">
                  Please enter a valid URL starting with https:// or http://
                </div>
              )}
            </div>
          )}

          <div className="mt-2">
            <button
              onClick={handleAddEvent}
              className="w-full flex items-center justify-center py-1 px-2 bg-[#2B2C2F] hover:bg-[#2B2C2F] rounded text-xs text-gray-300"
            >
              Add Event
            </button>
            {noTriggerError && (
              <div className="mt-1 text-[10px] text-[#EE2B2A] bg-[#EE2B2A]/10 border border-[#EE2B2A]/30 rounded px-2 py-1 text-center">
                ⚠ No triggers available. All trigger types are already used for this object.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Display existing events */}
      {events.map((event, index) => {
        const triggerId =
          event.triggeringObject ||
          activeObject?._id ||
          activeObject?.name ||
          activeObject?.url;

        const triggeringAsset = allAssets.find(
          (a) =>
            a._id === triggerId ||
            a.id === triggerId ||
            a.url === triggerId ||
            a.name === triggerId ||
            a.temp_file_name === triggerId
        );
        const triggeringDisplayName =
          triggeringAsset?.file_name ||
          triggeringAsset?.asset_file_name ||
          triggeringAsset?.url ||
          activeObject?.file_name ||
          activeObject?.name ||
          activeObject?.url ||
          'Unknown';

        // Get target object name if exists
        const targetAsset = allAssets.find(
          (a) =>
            a._id === event.affectedAsset || a.id === event.affectedAsset
        );
        const targetDisplayName = targetAsset
          ? targetAsset.file_name ||
          targetAsset.asset_file_name ||
          targetAsset.url ||
          targetAsset.name ||
          'Unknown'
          : '';

        return (
          <div
            key={event.id || index}
            ref={(el) => {
              if (!event.id) return;
              if (el) eventRowRefs.current.set(event.id, el);
              else eventRowRefs.current.delete(event.id);
            }}
            className="flex flex-col py-1 text-[10px] text-gray-400"
          >
            <SimpleAssetTooltip offsetLeft={-90} label={triggeringDisplayName}>
              <span className="mb-1 block max-w-[120px] truncate">
                {triggeringDisplayName}
              </span>
            </SimpleAssetTooltip>
            <div className="flex items-center flex-nowrap">
              <div className="bg-[#2B2C2F] text-[10px] min-w-[30px] text-gray-400 py-1 px-2 rounded w-full truncate">
                {normalizeEventTrigger(event.trigger)}
              </div>
              <img
                src={addcomponent.group366}
                className="mx-2 w-3 h-3"
                alt="arrow"
              />
              <SimpleAssetTooltip
                className="w-full min-w-0"
                offsetLeft={-200}
                label={`${event.action}${event.affectedAssetModel && targetDisplayName
                    ? ` ${targetDisplayName}`
                    : ''
                  }${event.url ? ` → ${event.url}` : ''}`}
              >
                <div className="bg-[#2B2C2F] text-[10px] text-gray-400 min-w-[30px] py-1 px-2 rounded w-full flex">
                  <span className="truncate w-full block">
                    {event.action}
                    {event.affectedAssetModel && targetDisplayName && (
                      <span className="text-[9px] opacity-75">
                        {' '}
                        {targetDisplayName.length > 8
                          ? '...' +
                          targetDisplayName.substring(
                            targetDisplayName.length - 5
                          )
                          : targetDisplayName}
                      </span>
                    )}
                    {event.url && (
                      <span className="text-[9px] opacity-75">
                        →{' '}
                        {event.url.length > 15
                          ? '...' + event.url.substring(event.url.length - 12)
                          : event.url}
                      </span>
                    )}
                  </span>
                </div>
              </SimpleAssetTooltip>
              <button
                onClick={() => {
                  setEventToDelete(event.id);
                  setIsDeleteModalOpen(true);
                }}
                className="w-6 h-6 flex items-center justify-center"
              >
                <img
                  className="min-w-4 min-h-4 ml-2"
                  src={addcomponent.group368}
                  alt="remove"
                />
              </button>
            </div>
          </div>
        );
      })}

      <ConfirmationModel
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setEventToDelete(null);
        }}
        onConfirm={() => {
          if (eventToDelete) {
            handleRemoveEvent(eventToDelete);
          }
          setIsDeleteModalOpen(false);
          setEventToDelete(null);
        }}
        message="Are you sure you want to delete this event?"
      />

      <ConfirmationModel
        isOpen={isReplaceModalOpen}
        onClose={handleCancelReplaceEvent}
        onConfirm={handleConfirmReplaceEvent}
        title={VR_MR_REPLACE_CONFIRM_TITLE}
        message={VR_MR_REPLACE_CONFIRM_MESSAGE}
        confirmText="Replace"
        cancelText="Cancel"
      />
    </div>
  );
};

export default EventsComponent;
